import { useI18n } from '@/hooks/useI18n';
import { useState } from 'react';
import { Flame, Play, RefreshCw, Shield, UserCheck } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  useTriggerCycle,
  useTriggerProfileRefresh,
  useTriggerRollupDaily,
  useTriggerRollupHourly,
  useTriggerViralDetection,
} from '@/hooks/mutations/useAdminTriggers';

interface Trigger {
  id: string;
  label: string;
  description: string;
  icon: LucideIcon;
  pending: boolean;
  run: () => Promise<unknown>;
}

export function TriggersPanel() {
  const { t } = useI18n();
  const cycle = useTriggerCycle();
  const profile = useTriggerProfileRefresh();
  const hourly = useTriggerRollupHourly();
  const daily = useTriggerRollupDaily();
  const viral = useTriggerViralDetection();

  const [confirmId, setConfirmId] = useState<string | null>(null);

  const triggers: Trigger[] = [
    {
      id: 'cycle',
      label: t('Cycle principal', 'Main cycle'),
      description: t(
        'Déclenche un scrape immédiat. Consomme du quota.',
        'Starts an immediate scrape. Uses quota.',
      ),
      icon: Play,
      pending: cycle.isPending,
      run: () => cycle.mutateAsync(),
    },
    {
      id: 'profile',
      label: 'Profile refresh',
      description: t(
        'Met à jour followers/bio via le job user_info.',
        'Updates followers/bio using the user_info job.',
      ),
      icon: UserCheck,
      pending: profile.isPending,
      run: () => profile.mutateAsync(),
    },
    {
      id: 'rollup-h',
      label: t('Rollup horaire', 'Hourly rollup'),
      description: t(
        'Recalcule les rollups horaires (heure précédente).',
        'Recalculates hourly rollups (previous hour).',
      ),
      icon: RefreshCw,
      pending: hourly.isPending,
      run: () => hourly.mutateAsync({}),
    },
    {
      id: 'rollup-d',
      label: t('Rollup journalier', 'Daily rollup'),
      description: t(
        'Recalcule les rollups journaliers (hier UTC).',
        'Recalculates daily rollups (yesterday UTC).',
      ),
      icon: RefreshCw,
      pending: daily.isPending,
      run: () => daily.mutateAsync({}),
    },
    {
      id: 'viral',
      label: t('Détection virale', 'Viral detection'),
      description: t(
        'Force un pass de détection des reels viraux.',
        'Forces a viral reel detection pass.',
      ),
      icon: Flame,
      pending: viral.isPending,
      run: () => viral.mutateAsync(),
    },
  ];

  const confirm = triggers.find((trigger) => trigger.id === confirmId) ?? null;

  const confirmRun = async () => {
    if (!confirm) return;
    try {
      await confirm.run();
    } finally {
      setConfirmId(null);
    }
  };

  return (
    <section className="space-y-2">
      <header className="flex items-baseline justify-between">
        <h3 className="text-[14px] font-semibold tracking-tight text-text-1">
          {t('Déclencheurs', 'Triggers')}
        </h3>
        <span className="inline-flex items-center gap-1 rounded-full bg-violet-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-violet-deep">
          <Shield className="h-2.5 w-2.5" />
          admin
        </span>
      </header>
      <div className="space-y-1.5 rounded-[14px] border border-line-1 bg-bg-1 p-2 shadow-card">
        {triggers.map((trigger) => {
          const Icon = trigger.icon;
          return (
            <button
              key={trigger.id}
              type="button"
              onClick={() => setConfirmId(trigger.id)}
              disabled={trigger.pending}
              className="group flex min-h-11 w-full items-center gap-2.5 rounded-[10px] px-2.5 py-2 text-left transition-colors hover:bg-bg-2 disabled:opacity-50"
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] bg-violet-soft text-violet-deep">
                <Icon className="h-3.5 w-3.5" />
              </span>
              <span className="min-w-0 flex-1 text-[12px] font-medium text-text-1">
                {trigger.label}
              </span>
              <span className="shrink-0 text-[11px] text-text-3 transition-colors group-hover:text-violet-deep">
                {t('Exécuter ▸', 'Run ▸')}
              </span>
            </button>
          );
        })}
      </div>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && setConfirmId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('Confirmer :', 'Confirm:')} {confirm?.label}
            </DialogTitle>
            <DialogDescription>{confirm?.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmId(null)}>
              {t('Annuler', 'Cancel')}
            </Button>
            <Button variant="primary" onClick={confirmRun} disabled={confirm?.pending}>
              {t('Exécuter', 'Run')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
